import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { getHealthMetricsFromTests } from '../lib/supabase-patient-api';
import { HealthMetrics, HealthMetricPoint, HealthMetricsWithLatest } from '../types/health-metric-types';
import { useEffect } from 'react';

// Export the key types for external use
export type { HealthMetrics, HealthMetricPoint, HealthMetricsWithLatest };

// Define possible structures for the results object
type TestResultData = {
  systolic?: number | string;
  diastolic?: number | string;
  overall_avg?: string;
  morning_avg?: string;
  evening_avg?: string;
  weight?: number | string;
  current_weight?: number | string;
  hba1c?: number | string;
  hemoglobin_a1c?: number | string;
} | Record<string, unknown>; // Allow other properties

// Define a default/initial state that matches the HealthMetricsWithLatest structure
const defaultMetricsWithLatest: HealthMetricsWithLatest = {
  bloodPressureSystolic: [],
  bloodPressureDiastolic: [],
  weight: [],
  hemoglobinA1c: [],
  heartRate: [],
  temperature: [],
  oxygenSaturation: [],
  // Initialize latest values as null
  latestBloodPressureSystolic: null,
  latestBloodPressureDiastolic: null,
  latestWeight: null,
  latestHemoglobinA1c: null,
  latestHeartRate: null,
  latestTemperature: null,
  latestOxygenSaturation: null,
};

// Query keys
export const healthMetricKeys = {
  all: ['healthMetrics'] as const,
  timeseries: (userId: string | undefined) => [...healthMetricKeys.all, 'timeseries', userId] as const,
};

// Helper to parse a blood pressure string like "120/80" into systolic and diastolic values
const parseBloodPressure = (bpString: string): { systolic: number | null, diastolic: number | null } => {
  if (!bpString) return { systolic: null, diastolic: null };
  
  const parts = bpString.split('/');
  if (parts.length !== 2) return { systolic: null, diastolic: null };
  
  const systolic = parseInt(parts[0].trim(), 10);
  const diastolic = parseInt(parts[1].trim(), 10);
  
  return {
    systolic: isNaN(systolic) ? null : systolic,
    diastolic: isNaN(diastolic) ? null : diastolic
  };
};

// Helper to safely parse a value from a JSON field
// Commented out as it's not currently used but may be useful later
/* 
const safelyParseJsonValue = (jsonStr: string | null, key: string): number | null => {
  if (!jsonStr) return null;
  
  try {
    const data = JSON.parse(jsonStr);
    const value = data[key];
    return typeof value === 'number' ? value : 
           typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : null;
  } catch (e) {
    console.warn(`Error parsing JSON value for ${key}:`, e);
    return null;
  }
};
*/

// Function to transform test results into structured health metrics
const transformTestResultsToMetrics = (
  bpTests: any[],
  weightTests: any[],
  hba1cTests: any[]
): HealthMetrics => {
  const metrics: HealthMetrics = { ...defaultMetricsWithLatest };
  
  // Process blood pressure data
  bpTests.forEach((test) => {
    if (!test.results) {
      // console.log(`BP Test ${index}: Skipping, no results field.`);
      return;
    }

    // console.log(`BP Test ${index}: Has results:`, typeof test.results, test.results);
    try {
      // Ensure we have a proper object regardless of the input format
      let resultsObj: TestResultData = {}; // Use the new type
      if (typeof test.results === 'string') {
        try {
          const parsed = JSON.parse(test.results);
          // Basic check if parsed result is an object
          if (typeof parsed === 'object' && parsed !== null) {
            resultsObj = parsed;
          } else {
            //  console.warn(`BP Test ${index}: Parsed string is not an object:`, parsed);
          }
          // console.log(`BP Test ${index}: Successfully parsed results string:`, resultsObj);
        } catch (parseError) {
          // console.error(`BP Test ${index}: Error parsing results string:`, parseError);
          // Keep resultsObj as {}
        }
      } else if (typeof test.results === 'object' && test.results !== null) { // Ensure it's a non-null object
        resultsObj = test.results;
        // console.log(`BP Test ${index}: Results already an object:`, resultsObj);
      } else {
        // console.log(`BP Test ${index}: Results in unexpected format or null:`, typeof test.results);
         // Keep resultsObj as {}
      }

      // Use type guards or optional chaining for safer access
      if (resultsObj && typeof resultsObj.systolic !== 'undefined' && typeof resultsObj.diastolic !== 'undefined') {
        const systolicInput = resultsObj.systolic;
        const diastolicInput = resultsObj.diastolic;

        if ((typeof systolicInput === 'string' || typeof systolicInput === 'number') && 
            (typeof diastolicInput === 'string' || typeof diastolicInput === 'number')) {
          
          const systolicValue = Number(systolicInput);
          const diastolicValue = Number(diastolicInput);

          // Add validation before pushing
          if (!isNaN(systolicValue) && !isNaN(diastolicValue)) {
            metrics.bloodPressureSystolic.push({ date: test.completed_date || test.ordered_date, value: systolicValue });
            metrics.bloodPressureDiastolic.push({ date: test.completed_date || test.ordered_date, value: diastolicValue });
          } else {
            // console.log(`BP Test ${index}: Invalid numeric values after conversion: systolic=${systolicValue}, diastolic=${diastolicValue}`);
          }
        } else {
          // console.log(`BP Test ${index}: systolic or diastolic input is not a string or number.`);
        }
      } else if (resultsObj && (resultsObj.overall_avg || resultsObj.morning_avg || resultsObj.evening_avg)) {
        // Prioritize overall_avg, then morning_avg, then evening_avg
        const bpString = resultsObj.overall_avg ?? resultsObj.morning_avg ?? resultsObj.evening_avg ?? '';
        // console.log(`BP Test ${index}: Found avg string: ${bpString}`);
        // Ensure bpString is actually a string before parsing
        if (typeof bpString === 'string') {
            const { systolic, diastolic } = parseBloodPressure(bpString);
            // console.log(`BP Test ${index}: Parsed avg string:`, { systolic, diastolic });
            const date = test.completed_date || test.ordered_date;
            if (systolic !== null && !isNaN(systolic) && date) metrics.bloodPressureSystolic.push({ date, value: systolic });
            if (diastolic !== null && !isNaN(diastolic) && date) metrics.bloodPressureDiastolic.push({ date, value: diastolic });
        } else {
            // console.warn(`BP Test ${index}: bpString derived from averages is not a string:`, bpString);
        }
      } else {
        // console.log(`BP Test ${index}: No matching BP format in results.`);
      }
    } catch {
      // Silently continue with other test records
    }
  });
  
  // Process weight data
  weightTests.forEach((test) => {
    if (!test.results) {
      // console.log(`Weight Test ${index}: Skipping, no results field.`);
      return;
    }

    // console.log(`Weight Test ${index}: Has results:`, typeof test.results, test.results);
    try {
      // Ensure we have a proper object regardless of the input format
      let resultsObj: TestResultData = {}; // Use the new type
      if (typeof test.results === 'string') {
        try {
           const parsed = JSON.parse(test.results);
            // Basic check if parsed result is an object
            if (typeof parsed === 'object' && parsed !== null) {
              resultsObj = parsed;
            } else {
              //  console.warn(`Weight Test ${index}: Parsed string is not an object:`, parsed);
            }
          // console.log(`Weight Test ${index}: Successfully parsed results string:`, resultsObj);
        } catch (parseError) {
          // console.error(`Weight Test ${index}: Error parsing results string:`, parseError);
          // Keep resultsObj as {}
        }
      } else if (typeof test.results === 'object' && test.results !== null) { // Ensure it's a non-null object
        resultsObj = test.results;
        // console.log(`Weight Test ${index}: Results already an object:`, resultsObj);
      } else {
        // console.log(`Weight Test ${index}: Results in unexpected format or null:`, typeof test.results);
         // Keep resultsObj as {}
      }

      // Prioritize weight, then current_weight using optional chaining and nullish coalescing
      const weightValue = resultsObj?.weight ?? resultsObj?.current_weight;
      // console.log(`Weight Test ${index}: Extracted weightValue:`, weightValue);

      if (typeof weightValue !== 'undefined' && weightValue !== null) { // Check for existence
        const numericWeight = typeof weightValue === 'string'
          ? parseFloat(weightValue.replace(/[^\d.-]/g, ''))
          : typeof weightValue === 'number' ? weightValue : NaN;

        if (!isNaN(numericWeight)) {
          metrics.weight.push({ date: test.completed_date || test.ordered_date, value: numericWeight });
        }
      }
    } catch {
      // Silently continue with other test records
    }
  });
  
  // Process HbA1c data
  hba1cTests.forEach((test) => {
    if (!test.results) {
      // console.log(`HbA1c Test ${index}: Skipping, no results field.`);
      return;
    }

    // console.log(`HbA1c Test ${index}: Has results:`, typeof test.results, test.results);
    try {
      // Ensure we have a proper object regardless of the input format
      let resultsObj: TestResultData = {}; // Use the new type
      if (typeof test.results === 'string') {
        try {
           const parsed = JSON.parse(test.results);
            // Basic check if parsed result is an object
            if (typeof parsed === 'object' && parsed !== null) {
              resultsObj = parsed;
            } else {
              //  console.warn(`HbA1c Test ${index}: Parsed string is not an object:`, parsed);
            }
          // console.log(`HbA1c Test ${index}: Successfully parsed results string:`, resultsObj);
        } catch (parseError) {
          // console.error(`HbA1c Test ${index}: Error parsing results string:`, parseError);
          // Keep resultsObj as {}
        }
      } else if (typeof test.results === 'object' && test.results !== null) { // Ensure it's a non-null object
        resultsObj = test.results;
        // console.log(`HbA1c Test ${index}: Results already an object:`, resultsObj);
      } else {
        // console.log(`HbA1c Test ${index}: Results in unexpected format or null:`, typeof test.results);
         // Keep resultsObj as {}
      }

      // Prioritize hba1c, then hemoglobin_a1c using optional chaining and nullish coalescing
      const hba1cValue = resultsObj?.hba1c ?? resultsObj?.hemoglobin_a1c;
      // console.log(`HbA1c Test ${index}: Extracted hba1cValue:`, hba1cValue);

      if (typeof hba1cValue !== 'undefined' && hba1cValue !== null) { // Check for existence
        const numericValue = typeof hba1cValue === 'string'
          ? parseFloat(hba1cValue.replace(/[^\d.-]/g, '')) // Remove non-numeric chars before parsing
          : typeof hba1cValue === 'number' ? hba1cValue : NaN;
        // console.log(`HbA1c Test ${index}: Parsed numericValue:`, numericValue);

        if (!isNaN(numericValue)) {
          metrics.hemoglobinA1c.push({ date: test.completed_date || test.ordered_date, value: numericValue });
          // console.log(`HbA1c Test ${index}: Successfully added HbA1c value to metrics`);
        }
      }
    } catch (e) {
      // console.warn(`Error processing HbA1c Test ${index}:`, e);
    }
  });
  
  return metrics;
};

// Helper function to extract latest values from the fetched historical arrays
const extractLatestMetricsFromArrays = (metrics: HealthMetrics): Partial<HealthMetricsWithLatest> => {
    const latest: Partial<HealthMetricsWithLatest> = {};

    // Sort by date descending and take the first value
    const getLatest = (arr: HealthMetricPoint[]): number | null => {
        if (!arr || arr.length === 0) return null;
        const sorted = [...arr].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        return sorted[0].value;
    };

    latest.latestBloodPressureSystolic = getLatest(metrics.bloodPressureSystolic);
    latest.latestBloodPressureDiastolic = getLatest(metrics.bloodPressureDiastolic);
    latest.latestWeight = getLatest(metrics.weight);
    latest.latestHemoglobinA1c = getLatest(metrics.hemoglobinA1c);
    latest.latestHeartRate = getLatest(metrics.heartRate || []);
    latest.latestTemperature = getLatest(metrics.temperature || []);
    latest.latestOxygenSaturation = getLatest(metrics.oxygenSaturation || []);

    return latest;
};

/**
 * Custom hook to fetch time-series and latest health metrics for the logged-in user.
 */
export const useHealthMetrics = () => {
  const authContext = useAuth();
  const user = authContext?.user;
  const userId = user?.id;
  const queryClient = useQueryClient();
  
  // Force refetch once to ensure we have fresh data
  useEffect(() => {
    if (userId) {
      // Invalidate and refetch on component mount
      queryClient.invalidateQueries({ queryKey: healthMetricKeys.timeseries(userId) });
    }
  }, [userId, queryClient]);
  
  // Update the query to expect the new combined type
  const queryResult = useQuery<HealthMetricsWithLatest, Error>({
    queryKey: healthMetricKeys.timeseries(userId),
    queryFn: async (): Promise<HealthMetricsWithLatest> => {
      if (!userId) {
        return defaultMetricsWithLatest;
      }
      
      // Fetch historical test results
      const rawData = await getHealthMetricsFromTests(userId);
      
      // Transform the historical data
      const historicalMetrics = transformTestResultsToMetrics(
          rawData.bloodPressure || [], 
          rawData.weight || [], 
          rawData.hba1c || []
      );
      
      // Extract latest data from the transformed historical arrays
      const latestMetrics = extractLatestMetricsFromArrays(historicalMetrics);

      // Combine historical and latest
      return {
          ...historicalMetrics,
          ...latestMetrics,
      };
    },
    enabled: !!userId,
    staleTime: 15 * 60 * 1000, // 15 minutes
    initialData: defaultMetricsWithLatest,
  });

  return {
    ...queryResult,
    // Rename metrics to indicate it includes latest values
    healthData: queryResult.data ?? defaultMetricsWithLatest, 
  };
}; 